import { ENV } from "@/config/env";
import { getPassportToken } from "@/services/auth/tokenStore";
import { authEventEmitter } from "@/services/auth/authEvents";
import type { AvailablePhone, CallCredentials, CallFeedback, GenerateCallTokenRequest } from "./types";

/**
 * Shown when a WhatsApp call request fails without the backend explaining why.
 * Lives here so the data layer and both `ChannelHeader` variants word it once.
 */
export const CALL_REQUEST_FAILED_MESSAGE =
    "The call request could not be sent. Please try again.";

/** Shown when the request goes out but the backend returns no message. */
export const CALL_REQUEST_SENT_MESSAGE =
    "The client has been asked for permission to receive a WhatsApp call.";

/** Shown when the number list can't be loaded and the backend gives no reason. */
export const PHONES_LOAD_FAILED_MESSAGE =
    "Could not load the client's phone numbers. Please try again.";

/** Shown when the post-call feedback can't be saved and the backend gives no reason. */
export const CALL_FEEDBACK_FAILED_MESSAGE =
    "Your feedback could not be sent. Please try again.";

/**
 * Builds the human-readable message for a failed call-endpoint response.
 *
 * The call endpoints answer with `{ message, errors }` where `errors` is often
 * a single explanatory string (e.g. 422 `message: "Phone number not found"`,
 * `errors: "This client does not support selecting a specific phone number"`).
 * The `message` alone is too terse to act on, so the string detail is appended.
 * Validation-style `errors` objects are left out — `message` covers those.
 */
function describeCallError(errorData: any, fallback: string): string {
    const message: string =
        typeof errorData?.message === 'string' && errorData.message.trim()
            ? errorData.message.trim()
            : fallback;
    const detail = typeof errorData?.errors === 'string' ? errorData.errors.trim() : '';
    if (!detail || detail === message) return message;
    return `${message.replace(/[.!]+$/, '')}. ${detail}`;
}

/**
 * Requests a LiveKit room + access token from Universe for a call.
 *
 * Pure data layer — no LiveKit/UI imports — so it can be unit-tested and reused.
 * Follows the same auth + 401 conventions as [services/universe/client.ts].
 *
 * `phone_id` is only ever sent on the `pstn` channel and only when it is a real
 * integer — the backend ignores it on `whatsapp`, and a Lead rejects it (422),
 * so the body is rebuilt here instead of forwarding `params` blindly.
 *
 * @throws Error on a missing passport token, an auth failure, or a malformed
 *   response. The caller (hook/UI) is responsible for surfacing the message.
 */
export async function generateCallToken(
    params: GenerateCallTokenRequest
): Promise<CallCredentials> {
    const passportToken = await getPassportToken();

    if (!passportToken) {
        throw new Error("No passport token found");
    }

    const body: GenerateCallTokenRequest = {
        channel: params.channel,
        channel_id: params.channel_id,
    };
    if (params.channel === 'pstn' && Number.isInteger(params.phone_id)) {
        body.phone_id = params.phone_id;
    }

    const response = await fetch(`${ENV.API_BASE_URL}/api/stream/call/generate/token`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${passportToken}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        if (response.status === 401) {
            authEventEmitter.emitLogout();
            throw new Error("Your session has expired. Please sign in again.");
        }
        const errorData = await response.json().catch(() => ({}));
        throw new CallApiError(
            describeCallError(errorData, `Failed to generate call token: ${response.statusText}`),
            response.status,
        );
    }

    const data = await response.json();
    const credentials = data?.data;

    if (!credentials?.roomName || !credentials?.token) {
        throw new Error(data?.message || "Call token response missing roomName or token");
    }

    return { roomName: credentials.roomName, token: credentials.token };
}

/**
 * Lists the numbers the channel's client can be called on over PSTN, so the
 * guide can pick one and its `id` goes out as `phone_id` in [generateCallToken].
 *
 * `GET /api/stream/channels/{channelId}/available-phones` (Passport bearer).
 * A Student returns one entry per `phones` row (possibly none — they are still
 * callable through the WhatsApp-number fallback); a Lead returns exactly one
 * entry with `id: null`. The picker is only shown when there is more than one
 * entry, so the single-number/empty paths keep dialling without `phone_id`.
 *
 * @throws Error on a missing passport token, an auth failure, or a refused
 *   request (403 "You do not have permission to access the phones.", 404
 *   "Conversation not found for this channel."). The backend's own `message`
 *   is used so the caller can show it to the guide as-is.
 */
export async function fetchAvailablePhones(channelId: string): Promise<AvailablePhone[]> {
    const passportToken = await getPassportToken();

    if (!passportToken) {
        throw new Error("No passport token found");
    }

    const response = await fetch(
        `${ENV.API_BASE_URL}/api/stream/channels/${channelId}/available-phones`,
        {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${passportToken}`,
                'Accept': 'application/json',
            },
        }
    );

    if (!response.ok) {
        if (response.status === 401) {
            authEventEmitter.emitLogout();
            throw new Error("Your session has expired. Please sign in again.");
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
            errorData.message || `Failed to load phone numbers: ${response.statusText}`
        );
    }

    const data = await response.json().catch(() => null);
    if (data && data.success === false) {
        throw new Error(data.message || PHONES_LOAD_FAILED_MESSAGE);
    }

    const phones: unknown = data?.data;
    if (!Array.isArray(phones)) return [];
    return phones
        .filter((entry): entry is AvailablePhone => !!entry && typeof entry.phone === 'string' && entry.phone.trim() !== '')
        .map((entry) => ({
            id: Number.isInteger(entry.id) ? (entry.id as number) : null,
            phone: entry.phone,
            is_default: entry.is_default === true,
        }));
}

/**
 * Asks the other party (via the backend) for permission to place a WhatsApp
 * call. The channel's `custom_data.call_*` fields (see
 * [services/calls/callPermission.ts]) are updated by the backend once the party
 * responds; the client picks that up through Stream `channel.updated` events.
 *
 * The backend answers with its own human-readable `message` in both directions
 * (sent, or refused with `success: false` — see below); the caller shows that
 * message to the agent rather than inventing its own wording.
 *
 * @returns the backend's message when it supplies one, so the caller can
 *   confirm the request actually went out.
 * @throws Error on a missing passport token, a failed request, or a refusal.
 *   The caller is responsible for surfacing the message.
 */
export async function sendCallRequest(channelId: string): Promise<string | undefined> {
    const passportToken = await getPassportToken();

    if (!passportToken) {
        throw new Error("No passport token found");
    }

    const response = await fetch(
        `${ENV.API_BASE_URL}/api/stream/channels/${channelId}/message-call-request`,
        {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${passportToken}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        }
    );

    if (!response.ok) {
        if (response.status === 401) {
            authEventEmitter.emitLogout();
            throw new Error("Your session has expired. Please sign in again.");
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
            errorData.message || `Failed to send call request: ${response.statusText}`
        );
    }

    // The backend can refuse with HTTP 200 + success:false — either a real
    // refusal (WhatsApp's call-permission rate limit: "Cancelled to send, due
    // to out of limit.") or a benign one (permission is already granted, which
    // the backend then syncs to the channel as `call_status: approved`). Both
    // carry an explanatory `message`, so both are surfaced the same way.
    const data = await response.json().catch(() => null);
    if (data && data.success === false) {
        throw new Error(data.message || CALL_REQUEST_FAILED_MESSAGE);
    }

    return data?.message ?? undefined;
}

/**
 * A refused or failed call-endpoint request, carrying the HTTP status so a
 * caller can tell a refusal it must accept (4xx: "Voip not found.") from a
 * server failure worth retrying (5xx).
 */
export class CallApiError extends Error {
    constructor(message: string, readonly status: number) {
        super(message);
        this.name = 'CallApiError';
    }
}

/** Shown while the token request is being retried behind a still-closing call. */
export const PREVIOUS_CALL_CLOSING_MESSAGE = 'The previous call is still closing…';

/**
 * Whether a refused token request is the backend's "Call already ongoing with
 * this receiver" (400): a previous Voip for the same client is still open. That
 * state is usually transient — the row closes on LiveKit's `room_finished`
 * webhook 20–60 s after the last hang-up — so [useCallToken] retries it
 * automatically instead of showing a Retry button that can't succeed yet.
 *
 * Matched on the message text because the backend sends no error code; if the
 * wording changes this quietly degrades to the generic error path.
 */
export function isPreviousCallClosingError(error: unknown): boolean {
    return error instanceof CallApiError && error.status === 400 && /already ongoing/i.test(error.message);
}

/**
 * Tears down the call's backend legs (e.g. disconnects the bridged WhatsApp
 * party). Called when the guide hangs up — leaving the LiveKit room alone does
 * not end the bridged leg, so we must tell the backend explicitly.
 *
 * `POST /api/stream/call/terminate` with `{ room_name }` (the LiveKit room name
 * from the token response / `room.name`).
 *
 * `keepalive` (web only) lets the request outlive the page — the call window
 * or tab closing must still hang up the client's leg, and a normal fetch is
 * cancelled with the document. React Native's fetch ignores the option.
 *
 * Single attempt — the call screens go through [terminateCallWithRetry].
 *
 * @throws CallApiError on a refused/failed request (with the HTTP status),
 *   TypeError when fetch itself fails (no network), Error on a missing passport
 *   token. The caller is responsible for surfacing the message.
 */
export async function terminateCall(
    roomName: string,
    options: { keepalive?: boolean } = {},
): Promise<void> {
    const passportToken = await getPassportToken();

    if (!passportToken) {
        throw new Error("No passport token found");
    }

    const response = await fetch(`${ENV.API_BASE_URL}/api/stream/call/terminate`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${passportToken}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ room_name: roomName }),
        ...(options.keepalive ? { keepalive: true } : {}),
    });

    if (!response.ok) {
        if (response.status === 401) {
            authEventEmitter.emitLogout();
            throw new Error("Your session has expired. Please sign in again.");
        }
        const errorData = await response.json().catch(() => ({}));
        throw new CallApiError(
            errorData.message || `Failed to terminate call: ${response.statusText}`,
            response.status,
        );
    }
}

/**
 * Back-off between terminate attempts: five retries over ~30 s, enough to
 * outlive a brief network drop (the very thing that usually ends a call
 * abnormally) without keeping a dead request loop around for long.
 */
const TERMINATE_RETRY_DELAYS_MS = [2000, 4000, 8000, 8000, 8000];

/**
 * Whether a failed terminate is worth trying again: the request never reached
 * the backend (fetch rejects with a TypeError — "Network request failed" on
 * native, "Failed to fetch" / "Load failed" in browsers) or the backend fell
 * over (5xx). Refusals (4xx), auth failures and a missing token are final.
 */
export function isRetryableCallError(error: unknown): boolean {
    if (error instanceof CallApiError) return error.status >= 500;
    return error instanceof TypeError;
}

/**
 * [terminateCall] with retries. The guide's leg dropping is most often caused
 * by the network going away, so the one request that must get through is the
 * one most likely to fail: keep trying for a while so a short outage still
 * hangs up the client's leg once connectivity returns. The promise resolves on
 * the first success and rejects with the last error once the retries are used
 * up or the error is final.
 *
 * @param onRetry Called before each wait, with the error and the attempt number
 *   that just failed (1-based) — for logging.
 */
export async function terminateCallWithRetry(
    roomName: string,
    options: { keepalive?: boolean } = {},
    onRetry?: (error: unknown, attempt: number) => void,
): Promise<void> {
    for (let attempt = 0; ; attempt++) {
        try {
            await terminateCall(roomName, options);
            return;
        } catch (error) {
            const delay = TERMINATE_RETRY_DELAYS_MS[attempt];
            if (delay === undefined || !isRetryableCallError(error)) throw error;
            onRetry?.(error, attempt + 1);
            await new Promise((resolve) => setTimeout(resolve, delay));
        }
    }
}

/**
 * Saves the guide's post-call quality rating.
 *
 * `POST /api/stream/call/feedback` with `{ room_name, rating, issue }`
 * (Passport bearer). The backend writes it to the same `voip.quality_score` /
 * `quality_issue` columns the legacy Uniphone app fills, so it lands in the
 * existing call-quality reports. Sending again for the same room overwrites.
 *
 * @throws Error on a missing passport token, an auth failure, or a refused /
 *   failed request (message from the backend when it gives one). The caller
 *   (the feedback form) surfaces it and lets the guide retry or skip.
 */
export async function submitCallFeedback(roomName: string, feedback: CallFeedback): Promise<void> {
    const passportToken = await getPassportToken();

    if (!passportToken) {
        throw new Error("No passport token found");
    }

    const response = await fetch(`${ENV.API_BASE_URL}/api/stream/call/feedback`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${passportToken}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            room_name: roomName,
            rating: feedback.rating,
            issue: feedback.issue ?? null,
        }),
    });

    if (!response.ok) {
        if (response.status === 401) {
            authEventEmitter.emitLogout();
            throw new Error("Your session has expired. Please sign in again.");
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(describeCallError(errorData, CALL_FEEDBACK_FAILED_MESSAGE));
    }

    const data = await response.json().catch(() => null);
    if (data && data.success === false) {
        throw new Error(describeCallError(data, CALL_FEEDBACK_FAILED_MESSAGE));
    }
}

/**
 * Tells the backend that a call leg has been *answered*, so it can record the
 * authoritative participant state and start recording.
 *
 * Only the connected client can tell the real answer moment for a PSTN/SIP leg
 * (`sip.callStatus === 'active'`) — the LiveKit `participant_joined` webhook
 * can fire while the phone is still ringing (early media). So the app calls
 * this once it observes the leg is truly answered.
 *
 * `POST /api/stream/call/join` with `{ room_name, participant_identity }`
 * (Passport bearer). Idempotent on the backend, so retries are safe.
 *
 * @throws Error on a missing passport token or a failed request.
 */
export async function notifyCallJoin(roomName: string, participantIdentity: string): Promise<void> {
    const passportToken = await getPassportToken();

    if (!passportToken) {
        throw new Error("No passport token found");
    }

    const response = await fetch(`${ENV.API_BASE_URL}/api/stream/call/join`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${passportToken}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ room_name: roomName, participant_identity: participantIdentity }),
    });

    if (!response.ok) {
        if (response.status === 401) {
            authEventEmitter.emitLogout();
            throw new Error("Your session has expired. Please sign in again.");
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
            errorData.message || `Failed to notify call join: ${response.statusText}`
        );
    }
}
